import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { GRID_END_MINUTE, GRID_START_MINUTE, clockTime, minutesOfDay } from '@boud/contracts';
import { ApiClientError } from '../api/client';
import { listInvitablePeople } from '../api/directory';
import {
  createBooking,
  listBookableRooms,
  listBookings,
  type Booking,
  type Room,
} from '../api/rooms';
import { MonthGrid } from '../components/calendar/MonthGrid';
import { WeekGrid } from '../components/calendar/WeekGrid';
import { DayGrid } from '../components/calendar/DayGrid';
import { BookingDetailDialog } from '../components/BookingDetailDialog';
import { Modal } from '../components/Modal';
import { PeoplePicker } from '../components/PeoplePicker';
import { useToast } from '../components/Toasts';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../lib/icons';
import {
  addDays,
  addMonths,
  dateLabel,
  instantFor,
  monthGridDays,
  monthLabel,
  startOfWeek,
  timeLabel,
  todayInBuilding,
  weekDays,
} from '../lib/calendar';

type CalendarView = 'month' | 'week' | 'day';

/** What a drag or the "Book a room" button hands to the dialog. */
interface BookingDraft {
  roomId?: string;
  date: string;
  fromMinute: number;
  toMinute: number;
  /** True when the room came from a room column and must not be changed. */
  roomLocked: boolean;
}

/**
 * FEAT-006 — Room Booking.
 *
 * One screen, three views, and a single range endpoint behind all of them
 * (REQ-107). The view decides only which days to ask for.
 */
export function BookingsPage(): ReactElement {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  /*
   * Week, not month. A month cell can only say *that* a room is busy; the week
   * is the first view that shows when, which is what somebody opening this page
   * is nearly always trying to find out.
   */
  const [view, setView] = useState<CalendarView>('week');
  const [anchor, setAnchor] = useState(todayInBuilding());
  const [draft, setDraft] = useState<BookingDraft | null>(null);
  const [opened, setOpened] = useState<Booking | null>(null);

  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  const rooms = useQuery({ queryKey: ['bookable-rooms'], queryFn: listBookableRooms });

  const bookings = useQuery({
    queryKey: ['bookings', range.from, range.to],
    queryFn: () => listBookings({ from: range.from, to: range.to }),
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['bookings'] });
  }

  function shift(direction: -1 | 1): void {
    setAnchor((current) =>
      view === 'month'
        ? addMonths(current, direction)
        : addDays(current, direction * (view === 'week' ? 7 : 1)),
    );
  }

  const title =
    view === 'month'
      ? monthLabel(anchor, i18n.language)
      : view === 'week'
        ? `${dateLabel(startOfWeek(anchor), i18n.language)} — ${dateLabel(addDays(startOfWeek(anchor), 6), i18n.language)}`
        : dateLabel(anchor, i18n.language);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('nav_ops')}</div>
          <h1>{t('bookings')}</h1>
          <div className="lead">{t('bk_cal_lead')}</div>
        </div>
        <button
          type="button"
          className="btn primary"
          onClick={() =>
            setDraft({ date: anchor, fromMinute: 14 * 60, toMinute: 15 * 60, roomLocked: false })
          }
        >
          <Icon name="plus" />
          {t('bk_new')}
        </button>
      </div>

      <div className="note">
        <Icon name="info" />
        <div>{t('bk_drag_hint')}</div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="cal-nav" style={{ marginBottom: 0 }}>
            <button type="button" className="btn sm" onClick={() => setAnchor(todayInBuilding())}>
              {t('bk_today')}
            </button>
            {/*
              The chevron icon rather than the prototype's `‹` and `›` text.
              A glyph is at the mercy of whichever font resolves it — it renders
              at a different weight and baseline in Arabic than in English, and
              cannot be sized without changing the button's line box. The SVG is
              the same shape everywhere and scales with the button.
            */}
            <button
              type="button"
              className="cal-arrow"
              onClick={() => shift(-1)}
              aria-label={t('prev')}
            >
              <Icon name="chevron" />
            </button>
            <button
              type="button"
              className="cal-arrow next"
              onClick={() => shift(1)}
              aria-label={t('next')}
            >
              <Icon name="chevron" />
            </button>
            {/* Announced on change, so a screen-reader user hears where they moved to. */}
            <span className="cal-title" aria-live="polite">
              {title}
            </span>
          </div>

          <div className="head-tools">
            <div className="tabs" role="tablist">
              {(['month', 'week', 'day'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={view === value}
                  className={view === value ? 'tab on' : 'tab'}
                  onClick={() => setView(value)}
                >
                  {t(`cal_${value}`)}
                </button>
              ))}
            </div>
            <span className="hint">{t('bk_rooms_count', { count: rooms.data?.length ?? 0 })}</span>
          </div>
        </div>

        <div style={{ padding: '14px 18px 18px' }}>
          {bookings.isPending ? (
            <div className="empty" role="status" aria-live="polite">
              {t('loading')}
            </div>
          ) : bookings.error ? (
            <div className="empty" role="alert">
              {bookings.error instanceof ApiClientError
                ? bookings.error.message
                : t('login_network_error')}
            </div>
          ) : view === 'month' ? (
            <MonthGrid
              month={anchor}
              bookings={bookings.data ?? []}
              onOpen={setOpened}
              onOpenDay={(date) => {
                setAnchor(date);
                setView('day');
              }}
              /*
               * Clicking a day opens the same dialog the "Book a room" button
               * opens, with the date pre-filled. The room and time stay editable
               * because a month cell says nothing about either.
               */
              onBookDay={(date) =>
                setDraft({
                  date,
                  fromMinute: 14 * 60,
                  toMinute: 15 * 60,
                  roomLocked: false,
                })
              }
            />
          ) : view === 'week' ? (
            <WeekGrid
              date={anchor}
              bookings={bookings.data ?? []}
              onOpen={setOpened}
              onDraft={(d) => setDraft({ ...d, roomLocked: false })}
            />
          ) : (
            <DayGrid
              date={anchor}
              rooms={rooms.data ?? []}
              bookings={bookings.data ?? []}
              onOpen={setOpened}
              onDraft={(d) =>
                setDraft({
                  roomId: d.roomId,
                  date: anchor,
                  fromMinute: d.fromMinute,
                  toMinute: d.toMinute,
                  // The column *is* the room, so it is fixed.
                  roomLocked: true,
                })
              }
            />
          )}
        </div>
      </div>

      {draft ? (
        <BookingDialog
          draft={draft}
          rooms={rooms.data ?? []}
          onClose={() => setDraft(null)}
          onSaved={(booking) => {
            setDraft(null);
            refresh();
            notify(t('bk_created'));
            setOpened(booking);
          }}
        />
      ) : null}

      {opened ? (
        <BookingDetailDialog
          booking={opened}
          rooms={rooms.data ?? []}
          onClose={() => setOpened(null)}
          onChanged={() => {
            setOpened(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The days a view needs.
 *
 * The month view asks for the whole *grid*, not the month: its first and last
 * rows show days from the neighbouring months, and asking only for the month
 * would leave those cells wrongly empty.
 */
function rangeFor(view: CalendarView, anchor: string): { from: string; to: string } {
  if (view === 'month') {
    const days = monthGridDays(anchor);
    return { from: days[0] ?? anchor, to: days[days.length - 1] ?? anchor };
  }

  if (view === 'week') {
    const days = weekDays(anchor);
    return { from: days[0] ?? anchor, to: days[days.length - 1] ?? anchor };
  }

  return { from: anchor, to: anchor };
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

function BookingDialog({
  draft,
  rooms,
  onClose,
  onSaved,
}: {
  draft: BookingDraft;
  rooms: Room[];
  onClose: () => void;
  onSaved: (booking: Booking) => void;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const { notify } = useToast();

  const [title, setTitle] = useState('');
  const [roomId, setRoomId] = useState(draft.roomId ?? rooms[0]?.id ?? '');
  const [date, setDate] = useState(draft.date);
  const [from, setFrom] = useState(clockTime(draft.fromMinute));
  const [to, setTo] = useState(clockTime(draft.toMinute));
  const [inviteeIds, setInviteeIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);

  // Whoever is creating the booking is its organiser, and so cannot be one of
  // its invitees.
  const { user } = useAuth();

  const people = useQuery({
    queryKey: ['employees', 'invitable'],
    queryFn: listInvitablePeople,
  });

  const mutation = useMutation({
    mutationFn: () =>
      createBooking({
        room_id: roomId,
        title,
        starts_at: instantFor(date, minutesOfDay(from)),
        ends_at: instantFor(date, minutesOfDay(to)),
        invitee_ids: inviteeIds,
      }),
    onSuccess: onSaved,
    onError: (error) => {
      if (!(error instanceof ApiClientError)) return;

      setFieldErrors(error.fieldErrors);
      // A conflict, a past time or a closed room are not field-shaped problems —
      // they are about the slot as a whole, so they read as a banner.
      setBanner(error.message);
      notify(error.message, 'bad');
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    setBanner(null);
    mutation.mutate();
  }

  /*
   * The organiser is not in their own invitee list. The server refuses it, but
   * offering a name that can only produce an error is the kind of thing a person
   * reasonably reads as "I am attending" and ticks.
   */
  const selectable = (people.data ?? []).filter((p) => p.id !== user?.id);
  const lockedRoom = rooms.find((r) => r.id === draft.roomId);

  return (
    <Modal
      wide
      title={t('bk_new')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="booking-form"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('bk_confirm_title')}
          </button>
        </>
      }
    >
      <form id="booking-form" onSubmit={submit} noValidate>
        {banner ? (
          <div className="login-error" role="alert">
            <Icon name="info" />
            <span>{banner}</span>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="bk-title">{t('bk_purpose')}</label>
          <input
            id="bk-title"
            value={title}
            placeholder={t('bk_purpose')}
            onChange={(e) => setTitle(e.target.value)}
            className={fieldErrors.title ? 'bad' : ''}
            aria-invalid={Boolean(fieldErrors.title)}
          />
          {fieldErrors.title ? (
            <div className="field-error" role="alert">
              {fieldErrors.title}
            </div>
          ) : null}
        </div>

        {draft.roomLocked && lockedRoom ? (
          <>
            {/*
              Dragged from a room column, so room, date and time are already
              chosen and shown read-only — as the FRS specifies for this path.
            */}
            <div className="field">
              <div className="lbl">{t('bk_room')}</div>
              <div className="lockline">{lockedRoom.name}</div>
            </div>
            <div className="field">
              <div className="lbl">{t('bk_when')}</div>
              <div className="lockline">
                {dateLabel(date, i18n.language)} ·{' '}
                {timeLabel(instantFor(date, minutesOfDay(from)), i18n.language)}–
                {timeLabel(instantFor(date, minutesOfDay(to)), i18n.language)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label htmlFor="bk-room">{t('bk_room')}</label>
              <select id="bk-room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name} · {t('bk_cap')} {room.capacity}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="bk-date">{t('bk_date')}</label>
              <input
                id="bk-date"
                type="date"
                min={todayInBuilding()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className="grid2">
              <div className="field">
                <label htmlFor="bk-from">{t('bk_from')}</label>
                <input
                  id="bk-from"
                  type="time"
                  step={1800}
                  min={clockTime(GRID_START_MINUTE)}
                  max={clockTime(GRID_END_MINUTE)}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="bk-to">{t('bk_to')}</label>
                <input
                  id="bk-to"
                  type="time"
                  step={1800}
                  min={clockTime(GRID_START_MINUTE)}
                  max={clockTime(GRID_END_MINUTE)}
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        <PeoplePicker
          id="bk-invitees"
          label={t('bk_invite')}
          optionalNote={t('bk_invite_opt')}
          placeholder={t('bk_search_person')}
          people={selectable}
          selectedIds={inviteeIds}
          onChange={setInviteeIds}
        />
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------
